import { CustomizerEngine, DesignPersistence, DesignState } from 'smg/customizer';
import { KeepUploads } from 'smg/uploader/v2';
import { BackgroundColorUI, BackgroundColorUIState } from './background-color-ui';
import { ImageState, ImageUploadUI } from './image-upload-ui';
import { TextCustomizerUI, TextState } from './text-customizer-ui';
import { TextDropState, TextDropUI } from './text-drop-ui';

interface coinExtraState {
    textUIState: TextState;
    imageStates: ImageState[];
    backgroundColorState?: BackgroundColorUIState;
    plating?: string;
    textDropStates?: TextDropState;
}

/**
 * Coin-specific design persistence that extends the library's DesignPersistence.
 * Adds support for:
 * - Text UI state (ring settings, metal mode, font styles, separators)
 * - Image upload IDs (for calling keep endpoint to get permanent URLs)
 */
export class CoinDesignPersistence extends DesignPersistence {
    #textUI: TextCustomizerUI;
    #imageUI: ImageUploadUI;
    #bgColorUI: BackgroundColorUI;
    #textDropUI: TextDropUI;

    constructor(
        frontEngine: CustomizerEngine,
        backEngine: CustomizerEngine,
        textUI: TextCustomizerUI,
        imageUI: ImageUploadUI,
        bgColorUI: BackgroundColorUI,
        textDropUI: TextDropUI,
    ) {
        super(frontEngine, backEngine, { storageKey: 'coin-design-state', autoRestore: false });
        this.#textUI = textUI;
        this.#imageUI = imageUI;
        this.#bgColorUI = bgColorUI;
        this.#textDropUI = textDropUI;
    }

    /**
     * Add coin-specific UI state to the saved design.
     * Note: Image states are populated in beforeSave after getting permanent URLs.
     */
    protected override getExtraState(): Record<string, unknown> {
        const $checkedPlating = document.querySelector<HTMLInputElement>('input[name="plating-radio"]:checked');
        return {
            textUIState: this.#textUI.getState(),
            imageStates: [], // Populated in beforeSave
            backgroundColorState: this.#bgColorUI.getState(),
            plating: $checkedPlating?.value,
            textDropStates: this.#textDropUI.getState(),
        } satisfies coinExtraState;
    }

    protected override hasContent(state: DesignState): boolean {
        const extra = state.extra as coinExtraState | undefined;
        if (!extra) {
            return false;
        }

        // Has uploaded images — meaningful content
        if (extra.imageStates && extra.imageStates.length > 0) {
            return true;
        }

        // Check if any text has been customized beyond defaults
        const text = extra.textUIState;
        if (text) {
            const defaults = ['TOP TEXT', 'BOTTOM TEXT', ''];
            const hasCustomText = [
                text.front.topText,
                text.front.bottomText,
                text.back.topText,
                text.back.bottomText,
            ].some(t => !defaults.includes(t.trim()));
            if (hasCustomText) {
                return true;
            }
        }

        if (extra.backgroundColorState?.front.enabled || extra.backgroundColorState?.back.enabled) {
            return true;
        }

        const drops = extra.textDropStates;
        const hasDropText = (l: { text?: string }) => Boolean(l.text && l.text.trim().length > 0);
        if (drops && (drops.front.some(hasDropText) || drops.back.some(hasDropText))) {
            return true;
        }

        return false;
    }

    override clear(): void {
        this.#bgColorUI.reset();
        super.clear();
    }

    protected override async beforeSave(state: DesignState): Promise<void> {
        const uploadIDs = this.#imageUI.getUploadIDs();

        if (uploadIDs.length > 0) {
            const permanentUrls = await KeepUploads(uploadIDs);

            // Cache permanent URLs so we don't call keep again on subsequent saves
            if (permanentUrls.size > 0) {
                this.#imageUI.setPermanentUrls(permanentUrls);
            }
        }

        const imageStates = this.#imageUI.getState();

        if (state.extra) {
            (state.extra as unknown as coinExtraState).imageStates = imageStates;
        }
    }

    protected override async afterRestore(state: DesignState): Promise<void> {
        const extra = state.extra as coinExtraState | undefined;

        if (extra?.textUIState) {
            await this.#textUI.restoreState(extra.textUIState);
        }

        // Images are dynamic layers not part of engine state — restore separately
        if (extra?.imageStates && extra.imageStates.length > 0) {
            await this.#imageUI.restoreState(extra.imageStates);
        }

        // Text-drop blocks are dynamic layers too
        if (extra?.textDropStates) {
            await this.#textDropUI.restoreState(extra.textDropStates);
        }

        if (extra?.backgroundColorState) {
            this.#bgColorUI.restoreState(extra.backgroundColorState);
        }

        if (extra?.plating) {
            const $radio = document.querySelector<HTMLInputElement>(
                `input[name="plating-radio"][value="${CSS.escape(extra.plating)}"]`,
            );
            if ($radio) {
                $radio.checked = true;
                $radio.dispatchEvent(new Event('change'));
            }

            document.querySelectorAll<HTMLElement>(
                '#design-plating-brass, #design-plating-silver, #lg-plating-brass, #lg-plating-silver',
            ).forEach($card => {
                $card.classList.toggle('active', $card.dataset.value === extra.plating);
            });
        }
    }
}
